import React from "react";
import { FaCreditCard, FaLanguage, FaRocket, FaCheckCircle } from "react-icons/fa";
import { useInView } from "react-intersection-observer";
import { useTranslation } from 'react-i18next';
import { Link } from "react-router-dom";
import SEO from '../components/SEO';
import PageHero from '../components/page/PageHero';
import SectionHeading from '../components/page/SectionHeading';
import ProofStrip from '../components/page/ProofStrip';
import heroImage from '../assets/bookingWebsites/hero-terrace.webp';
import rhythmImage from '../assets/bookingWebsites/rhythm-seasons.webp';
import welcomeImage from '../assets/bookingWebsites/welcome-arrival.webp';

/**
 * /booking-websites
 *
 * Rebuilt onto the shared page kit. Beyond the styling — the page was on a
 * blue/cyan gradient that belonged to no other page on the site — three real
 * problems are fixed here:
 *
 *   1. `https://booking-example.com` was linked three times as "view the live
 *      example". It is a placeholder domain, not ours, and it answers 502.
 *      Sending a prospect to a broken site is worse than having no demo link,
 *      so those CTAs are gone until there is a real URL to point at.
 *   2. Two `<a href="/contact">` links dropped out of the SPA and reloaded the
 *      whole bundle. They are <Link>s now.
 *   3. `hover:${primaryTextColor}` interpolated a Tailwind class name, which
 *      the build cannot see and therefore never generated.
 *
 * Also removed: a render-time console.log and a useEffect whose timer callback
 * was an empty comment.
 */
const BookingWebsites = () => {
    const { t } = useTranslation();

    const primaryTextColor = "text-silver-light";
    const secondaryTextColor = "text-silver-medium";

    const revealOpts = { triggerOnce: true, threshold: 0.15, rootMargin: "-50px" };
    const [featuresRef, featuresInView] = useInView(revealOpts);
    const [contentRef, contentInView] = useInView(revealOpts);

    const features = [
        { key: "easyToServe", icon: <FaRocket className="text-2xl" /> },
        { key: "multiLanguage", icon: <FaLanguage className="text-2xl" /> },
        { key: "flexiblePayments", icon: <FaCreditCard className="text-2xl" /> },
    ];

    const included = ["professionalDesign", "mobileResponsive", "easyManagement", "readyToGoLive"];
    const businessTypes = [0, 1, 2, 3, 4, 5].map((i) =>
        t(`bookingWebsites.liveExample.businessTypes.${i}`)
    );

    return (
        <div className="min-h-screen bg-black font-sans">
            <SEO
                title="Custom Booking Website Development | Developer for Custom Booking System"
                description="Developer for custom booking system projects with fast UX, mobile conversion focus, and reliable payment flows. Perfect for service businesses that want more direct bookings and happy customers."
                keywords="developer for custom booking system, booking website development, appointment scheduling website, reservation system developer, custom web development services, website developer for business, booking platform Croatia"
                url="/booking-websites"
            />

            <PageHero
                eyebrow={t('bookingWebsites.hero.eyebrow', 'Booking websites')}
                title={t('bookingWebsites.hero.title')}
                subtitle={t('bookingWebsites.hero.subtitle')}
                ctaLabel={t('bookingWebsites.hero.contactUs')}
                ctaTo="/contact"
                secondaryLabel={t('bookingWebsites.hero.secondary', 'What you get')}
                secondaryHref="#what-you-get"
                proofLine={t('bookingWebsites.hero.proof', 'Rated 5.0 on Google · Free consultation · Fixed price agreed before work starts')}
                backgroundImage={heroImage}
            />

            <ProofStrip />

            {/* Features */}
            <section ref={featuresRef} className="py-20 bg-black">
                <div className="container mx-auto px-4">
                    <SectionHeading
                        eyebrow={t('bookingWebsites.features.eyebrow', 'Built for direct bookings')}
                        title={t('bookingWebsites.features.title')}
                        lead={t('bookingWebsites.features.subtitle')}
                        className="mb-12"
                    />

                    <div className="mx-auto grid max-w-6xl gap-6 md:grid-cols-3">
                        {features.map((feature, index) => (
                            <div
                                key={feature.key}
                                className={`flex flex-col rounded-lg border border-gray-800 bg-off-black p-8 transition-all duration-1000 transform hover:border-moss/50 ${featuresInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            >
                                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-full border border-moss/50 bg-moss-deep/30 text-moss-light">
                                    {feature.icon}
                                </div>
                                <h3 className={`mb-3 text-xl font-medium ${primaryTextColor}`}>
                                    {t(`bookingWebsites.features.${feature.key}.title`)}
                                </h3>
                                <p className={`${secondaryTextColor} flex-grow text-sm leading-relaxed`}>
                                    {t(`bookingWebsites.features.${feature.key}.description`)}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Rhythm band */}
            <section className="relative overflow-hidden border-y border-moss-deep/40">
                <img
                    src={rhythmImage}
                    alt=""
                    aria-hidden="true"
                    className="h-64 w-full object-cover md:h-80"
                    loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/30" />
                <div className="absolute inset-0 flex items-center">
                    <div className="container mx-auto px-4">
                        <p className="max-w-xl text-xl md:text-2xl font-serif text-silver-light leading-snug">
                            {t('bookingWebsites.rhythm.line', 'Your calendar, your prices, your rules — and no commission taken out of every booking.')}
                        </p>
                    </div>
                </div>
            </section>

            {/* What you get / who it suits */}
            <section id="what-you-get" ref={contentRef} className="scroll-mt-24 py-20 bg-black">
                <div className="container mx-auto px-4">
                    <SectionHeading
                        eyebrow={t('bookingWebsites.included.eyebrow', 'What you get')}
                        title={t('bookingWebsites.included.title', 'Everything a booking site needs, on day one')}
                        lead={t('bookingWebsites.included.lead', 'Built, tested and handed over ready to take real bookings — not a template you finish yourself.')}
                        className="mb-12"
                    />

                    <div
                        className={`mx-auto grid max-w-6xl items-start gap-12 md:grid-cols-2 transition-all duration-1000 transform ${contentInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                    >
                        <div>
                            <ul className="space-y-6">
                                {included.map((item) => (
                                    <li key={item} className="flex items-start gap-4">
                                        <FaCheckCircle className="mt-1 flex-shrink-0 text-moss" aria-hidden="true" />
                                        <div>
                                            <h3 className={`mb-1 font-medium ${primaryTextColor}`}>
                                                {t(`bookingWebsites.liveExample.${item}.title`)}
                                            </h3>
                                            <p className={`${secondaryTextColor} text-sm leading-relaxed`}>
                                                {t(`bookingWebsites.liveExample.${item}.description`)}
                                            </p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="rounded-lg border border-moss-deep/50 bg-moss-night p-8">
                            <h3 className={`mb-6 text-xl font-serif font-medium ${primaryTextColor}`}>
                                {t('bookingWebsites.liveExample.perfectFor')}
                            </h3>
                            <ul className="space-y-3">
                                {businessTypes.map((businessType) => (
                                    <li key={businessType} className={`flex items-center gap-3 ${secondaryTextColor}`}>
                                        <FaCheckCircle className="flex-shrink-0 text-moss" aria-hidden="true" />
                                        {businessType}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Closing CTA over the arrival band */}
            <section className="relative overflow-hidden">
                <img
                    src={welcomeImage}
                    alt=""
                    aria-hidden="true"
                    className="h-[26rem] w-full object-cover"
                    loading="lazy"
                />
                <div className="absolute inset-0 bg-black/70" />
                <div className="absolute inset-0 flex items-center">
                    <div className="container mx-auto px-4 text-center">
                        <h2 className={`mb-4 text-3xl md:text-4xl font-serif font-medium ${primaryTextColor}`}>
                            {t('bookingWebsites.cta.title')}
                        </h2>
                        <p className={`mx-auto mb-8 max-w-2xl ${secondaryTextColor}`}>
                            {t('bookingWebsites.cta.subtitle')}
                        </p>
                        <Link
                            to="/contact"
                            className="inline-block rounded-md bg-moss px-8 py-3 text-lg font-medium text-black transition-colors duration-300 hover:bg-moss-light"
                        >
                            {t('bookingWebsites.cta.contactUs')}
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default BookingWebsites;
